import type { AppGeoPerfRow } from '@/api/perf';

export type AppAgeSource = 'released' | 'created';
export type AppCohort = 'new' | 'old';

export interface AppAgeFields {
  appCreatedAt: string;
  appReleasedAt: string | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function dateOnly(value: string): string {
  return value.slice(0, 10);
}

export function appAgeAnchor(app: AppAgeFields): { date: string; source: AppAgeSource } {
  return app.appReleasedAt
    ? { date: dateOnly(app.appReleasedAt), source: 'released' }
    : { date: dateOnly(app.appCreatedAt), source: 'created' };
}

/** Inclusive calendar window ending on reportTo (30 means reportTo and 29 days before). */
export function appCohortCutoff(reportTo: string, newWindowDays: number): string {
  const days = Math.max(1, Math.floor(newWindowDays));
  const reportEndMs = Date.parse(`${reportTo}T00:00:00Z`);
  if (!Number.isFinite(reportEndMs)) return '';
  return new Date(reportEndMs - (days - 1) * DAY_MS)
    .toISOString().slice(0, 10);
}

export function appCohort(app: AppAgeFields, reportTo: string, newWindowDays: number): AppCohort {
  return appAgeAnchor(app).date >= appCohortCutoff(reportTo, newWindowDays) ? 'new' : 'old';
}

/** Combine country partitions into one funnel row per app. */
export function groupAppGeoRowsByApp(rows: AppGeoPerfRow[]): AppGeoPerfRow[] {
  const byApp = new Map<string, AppGeoPerfRow>();
  for (const row of rows) {
    const current = byApp.get(row.appId);
    if (!current) {
      byApp.set(row.appId, { ...row, country: 'ALL' });
      continue;
    }
    current.clicks += row.clicks;
    current.uniqClicks += row.uniqClicks;
    current.regs += row.regs;
    current.deps += row.deps;
    current.payoutCents += row.payoutCents;
  }
  return Array.from(byApp.values());
}
